import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid, Box, Typography, Paper, IconButton, Button,
} from '@material-ui/core';
import type { GridColumns } from '@material-ui/data-grid';
import {
  DataGrid,
} from '@material-ui/data-grid';
import type { AxiosError } from 'axios';
import axios from 'axios';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ScannerActionMenu from './ScannerActionMenu';
import globalUseStyles from '../../../globalClasses';

interface IScanner {
  scannerId: string;
  name: string;
  serialNumber: string;
  ipAddress: string;
  macAddress: string;
  location: string;
}

const ScanLookup: React.FC<unknown> = () => {
  const globalStyles = globalUseStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [scannerId, setScannerId] = React.useState<string | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const fetcher = (url: string) => axios.get<IScanner[]>(url)
    .then((response) => response.data)
    .catch(() => {
      enqueueSnackbar('Unable to retrieve scanners');
    });

  const { data, mutate } = useSWR(['/scanner/getScanners'], (url) => fetcher(url));

  const handleActionMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!anchorEl) {
      setScannerId(id);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleDeleteButtonClicked = () => {
    if (scannerId) {
      axios.post('/scanner/deleteScanner', { SerialNumber: scannerId })
        .then(() => {
          enqueueSnackbar('Scanner deleted.', { variant: 'success' });
          mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(`There was the following error when deleting scanner:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
        });
    } else {
      enqueueSnackbar('There is no scanner selected to delete');
    }
  };

  const handleAddScannerBtnClicked = () => {
    navigate('/scanner');
  };

  const handleClose = () => {
    setScannerId(null);
    setAnchorEl(null);
  };

  const displayVertIcon = (scannerIdParameter: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      id="table-kabob"
      onClick={(e) => handleActionMenuClick(e, scannerIdParameter)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const tableColumns: GridColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.75,
    },
    {
      field: 'serialNumber',
      headerName: 'Serial Number',
      flex: 1.25,
    },
    {
      field: 'ipAddress',
      headerName: 'IP Address',
      flex: 1,
    },
    {
      field: 'macAddress',
      headerName: 'MAC Address',
      flex: 1,
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
    },
    {
      field: 'scannerId',
      type: 'string',
      headerName: 'Actions',
      flex: 1,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  return (
    <Grid>
      <Grid item xs={12}>
        <Box py={3}>
          <Paper elevation={4}>
            <Grid container>
              <Box p={3}>
                <Grid item>
                  <Box pb={3} pt={3}>
                    <Typography variant="h4" className={globalStyles.labelText}>{'Scanners'}</Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <Grid container direction="row-reverse">
                    <Button color="primary" variant="contained" onClick={handleAddScannerBtnClicked}>{'ADD SCANNER'}</Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            {data
            && (
              <>
                <DataGrid
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  getRowId={(row) => ((row as IScanner).serialNumber)}
                  columns={tableColumns}
                  rows={data}
                  autoHeight
                  checkboxSelection
                  className={globalStyles.transparentDataGrid}
                />
                {
                  scannerId && anchorEl && (
                    <ScannerActionMenu
                      scannerId={scannerId}
                      anchorEl={anchorEl}
                      handleClose={handleClose}
                      handleDelete={handleDeleteButtonClicked}
                    />
                  )
                }
              </>
            )}
          </Paper>
        </Box>

      </Grid>
    </Grid>
  );
};

export default ScanLookup;
